import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import GlobalStyles from '@mui/material/GlobalStyles';
import EmailIcon from '@mui/icons-material/esm/AlternateEmail';
import LockIcon from '@mui/icons-material/esm/Lock';
import { AppDispatch, RootState } from '../../store';
import { login } from '../../store/slices/authSlice';
import { changePasswordPathForRole, fieldLandingForRole } from '../../routes/fieldAccess';
import fairmontLogo from '../../assets/branding/fairmont-estate-logo-mono-cream-1024.png';
import komikazoomFont from '../../assets/fonts/KAMIKZOM.ttf';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch() as AppDispatch;
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(login({ email, password })).unwrap();
      navigate(result.user?.requiresPasswordChange
        ? changePasswordPathForRole(result.user?.role)
        : fieldLandingForRole(result.user?.role));
    } catch (err) {
      // Error handled by Redux
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #f6f5f3 0%, #eeece9 100%)',
        color: '#1f1f1d',
        px: 2,
      }}
    >
      <GlobalStyles
        styles={{
          '@font-face': {
            fontFamily: 'Komikazoom',
            src: `url(${komikazoomFont}) format("truetype")`,
            fontWeight: 400,
            fontStyle: 'normal',
            fontDisplay: 'swap',
          },
        }}
      />
      <Container maxWidth="xs" disableGutters>
        <Box
          sx={{
            width: '100%',
            maxWidth: 380,
            mx: 'auto',
            py: { xs: 5, sm: 7 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              component="img"
              src={fairmontLogo}
              alt="Fairmont Estate"
              sx={{
                width: 138,
                height: 138,
                objectFit: 'contain',
                mb: 1,
                filter: 'brightness(0) drop-shadow(0 10px 18px rgba(35,35,33,0.10))',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Komikazoom, sans-serif',
                fontSize: { xs: 25, sm: 30 },
                fontWeight: 400,
                letterSpacing: 0,
                lineHeight: 1.05,
                color: '#1f1f1d',
              }}
            >
              YardStick
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #d5d1ca', mb: 2.25 }}>
              <Box sx={{ width: 42, minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7a7a75' }}>
                <EmailIcon fontSize="small" />
              </Box>
              <TextField
                fullWidth
                placeholder="E-MAIL"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={loading}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-root': { minHeight: 52, bgcolor: 'transparent' },
                  '& input': { fontSize: 12, fontWeight: 700, letterSpacing: 0, color: '#1f1f1d' },
                  '& input::placeholder': { color: '#888782', opacity: 1 },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #d5d1ca', mb: 2 }}>
              <Box sx={{ width: 42, minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7a7a75' }}>
                <LockIcon fontSize="small" />
              </Box>
              <TextField
                fullWidth
                placeholder="PASSWORD"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-root': { minHeight: 52, bgcolor: 'transparent' },
                  '& input': { fontSize: 12, fontWeight: 700, letterSpacing: 0, color: '#1f1f1d' },
                  '& input::placeholder': { color: '#888782', opacity: 1 },
                }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ fontSize: 12, fontWeight: 700, color: '#8b8984' }}>
                Forgot Password?
              </Link>
            </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
              sx={{
                minHeight: 54,
                borderRadius: 999,
                bgcolor: '#20201f',
                color: '#ffffff',
                boxShadow: '0 18px 34px rgba(35,35,33,0.16)',
                '&:hover': { bgcolor: '#111110', boxShadow: '0 20px 38px rgba(35,35,33,0.20)' },
              }}
              >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
              </Button>

          </Box>
        </Box>
      </Container>
    </Box>
  );
}
